<template>
    <v-treeview
        :items="sortedItems"
        activatable
        :activated="active"
        item-value="id"
        @update:activated="updateActive"
        :opened="open"
        @update:opened="onOpenedUpdate"
        color="secondary"
        expand-icon="mdi-chevron-right"
        collapse-icon="mdi-chevron-down"
        :load-children="loadChildren"
        items-registration="props"
    >
        <template v-slot:prepend="{ isOpen, item }">
            <div :id="item.id" class="mr-4" style="align-self: flex-start">
                <v-icon
                    v-if="!item.calcId"
                    color="accent"
                    :icon="
                        isOpen
                            ? 'mdi-folder-open-outline'
                            : 'mdi-folder-outline'
                    "
                />
                <v-icon
                    opacity="0.6"
                    v-else
                    icon="mdi-vector-polyline"
                    :style="[{ transform: 'rotate(90deg)' }, show[item.calcId]]"
                />
            </div>
        </template>
        <template v-slot:title="{ item }">
            <div class="treeview-title">
                <div
                    v-if="!item.calcId"
                    class="treeview-folder-title text-body-large"
                >
                    <span>
                        {{ item.name }}
                        <span v-if="item.count"> ({{ item.count }}) </span>
                    </span>
                </div>
                <div v-if="item.calcId" class="treeview-leaf-title">
                    <div style="flex-grow: 1; min-width: 0">
                        <div class="text-body-large">
                            <router-link
                                :to="{
                                    name: 'calculation',
                                    params: { calcId: item.calcId },
                                }"
                                style="text-decoration: none; color: inherit"
                                @click="goneToCalc = true"
                                >{{ item.name }}</router-link
                            >
                        </div>
                        <div>
                            <span
                                v-if="item.description"
                                class="
                                    calc-description
                                    text-label-medium
                                    font-weight-regular
                                    text-medium-emphasis text-caption
                                "
                            >
                                {{ item.description }}
                            </span>
                        </div>
                    </div>

                    <div
                        style="
                            white-space: nowrap;
                            text-align: right;
                            flex-shrink: 0;
                        "
                    >
                        <v-lazy v-if="item.calcId" min-height="32">
                            <CalcStatsChips
                                :calcId="item.calcId"
                                :persistentCalcId="item.persistentCalcID"
                                :moveToEnd="true"
                                :noRightMargin="true"
                                :noWrap="true"
                                @toggle="like"
                            />
                        </v-lazy>
                        <div>
                            <v-btn
                                class="text-capitalize button text-label-medium"
                                rounded
                                variant="outlined"
                                density="comfortable"
                                @click="switchInfoShow(item.calcId)"
                            >
                                Info
                                <v-icon
                                    class="icon"
                                    size="x-large"
                                    color="secondary"
                                    icon="mdi-information-outline"
                                />
                            </v-btn>
                            <v-btn
                                class="
                                    text-capitalize
                                    button
                                    ml-2
                                    text-label-medium
                                "
                                rounded
                                density="comfortable"
                                variant="outlined"
                                @click="goToCalc(item)"
                            >
                                Open
                                <v-icon
                                    class="icon"
                                    size="large"
                                    color="secondary"
                                    icon="mdi-chevron-right"
                                />
                            </v-btn>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="show[item.calcId]" style="max-width: 1050px">
                <CalcInfo
                    :show="show[item.calcId]"
                    :calc="item"
                    :libraryVersion="getLibraryVersion(item)"
                />
            </div>
        </template>
    </v-treeview>
</template>

<script setup>
import CalcInfo from '../shared/CalcInfo.vue'
import CalcStatsChips from '../pages/calc/calc-info/CalcStatsChips.vue'
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import helpers from '../../helpers'

const props = defineProps({
    items: { required: true, type: Array },
    url: { required: false, default: false },
    firstOpen: { required: false, type: Array, default: () => [] },
    firstShow: { required: true },
    libraryVersions: { required: true },
    latest: { type: Boolean, required: true },
    calcs: { type: Array, required: true },
})

const store = useStore()
const router = useRouter()
const route = useRoute()

const open = ref([...props.firstOpen])
const active = ref([])
const lastCalcClosed = ref(null)
const show = ref({ ...props.firstShow })
const goneToCalc = ref(false)
const likeClicked = ref(false)
const noCalcsError = ref(false)

const sortedItems = computed(() => sortItems(props.items))

onMounted(() => {
    if (open.value.length > 1) {
        const item = open.value[open.value.length - 1]
        setTimeout(() => {
            const yOffset = -100
            const element = document.getElementById(item.toString())
            if (!element) return
            const y =
                element.getBoundingClientRect().top +
                window.pageYOffset +
                yOffset
            window.scrollTo({ top: y, behavior: 'smooth' })
        }, 500)
    }
})

function like() {
    likeClicked.value = true
}

function getLibraryVersion(calc) {
    return props.libraryVersions.find(
        (vers) => vers.libraryVersionId === calc.libraryVersionId
    )
}

function goToCalc(calc) {
    goneToCalc.value = true
    if (props.latest && calc.persistentCalcID) {
        router.push({
            name: 'persistentCalcId',
            params: { persistentId: calc.persistentCalcID },
        })
    } else {
        router.push({ name: 'calculation', params: { calcId: calc.calcId } })
    }
}

function switchInfoShow(id) {
    const dict = show.value
    dict[id] = !dict[id]
    if (!dict[id]) lastCalcClosed.value = id
    show.value = { ...dict }
    active.value = []
}

function updateActive(a) {
    if (likeClicked.value) {
        likeClicked.value = false
        active.value = []
        return
    }
    if (a[0] && a[0].split('-')[0] === 'c') {
        const calcId = a[0].split('-')[1]
        const fullCalc = props.calcs.find((c) => c.id === a[0])
        if (show.value[calcId]) {
            active.value = []
        } else if (parseInt(lastCalcClosed.value) === parseInt(calcId)) {
            active.value = []
            lastCalcClosed.value = null
        } else if (!goneToCalc.value) {
            goToCalc(fullCalc)
        }
    }
}

async function loadChildren(vers) {
    if (vers.children && vers.children.length > 0) return vers

    const calcs = await store.dispatch('getVersionCalc', vers.libraryVersionId)
    if (calcs && calcs.length > 0) {
        noCalcsError.value = false
        vers.children = helpers.buildTree(calcs)
        helpers.addCount(vers)
        const showUpdates = {}
        calcs.forEach((calc) => {
            showUpdates[calc.calcId] = false
        })
        show.value = { ...show.value, ...showUpdates }
        vers.children.forEach((node) => helpers.addCount(node))
        return vers
    } else {
        noCalcsError.value = true
        store.commit('SET_ALERT_ERRORS', [
            `The library version you're trying to use has no calcs. Please either check with the library owner, or ensure the library was uploaded in the right format.`,
        ])
    }
}

function onOpenedUpdate(opened) {
    open.value = opened
    updateUrl(opened)
}

function updateUrl(openArr) {
    if (openArr.length && props.url) {
        const opens = [...openArr]
        if (route.query.path && route.query.path.split('-')[0] === 'latest') {
            opens[0] = 'latest'
        }
        let path
        const lastOpenParts = opens[opens.length - 1]
        if (opens.length === 1) {
            path = opens[0]
        } else {
            const endPath = lastOpenParts
                .substring(lastOpenParts.indexOf('/') + 1)
                .replaceAll('/', '-')
            path = `${opens[0]}-${endPath}`
        }
        if (route.query.path !== path) {
            router.replace({ name: 'libraryBrowser', query: { path } })
        }
    }
}

function sortItems(items) {
    return [...items]
        .map((item) => {
            if (item.children && item.children.length > 0) {
                return { ...item, children: sortItems(item.children) }
            }
            return item
        })
        .sort((a, b) => {
            const isFolderA = a.children && a.children.length > 0
            const isFolderB = b.children && b.children.length > 0
            if (isFolderA && !isFolderB) return -1
            if (!isFolderA && isFolderB) return 1
            return a.name.localeCompare(b.name)
        })
}
</script>

<style>
/* Vuetify 4 treeview classes */
.v-treeview-item {
    box-shadow: 0px 3px 1px -2px rgb(0 0 0 / 20%),
        0px 2px 2px 0px rgb(0 0 0 / 14%), 0px 1px 5px 0px rgb(0 0 0 / 12%);
    margin-bottom: 5px;
}
.v-treeview-item .v-list-item__content {
    align-items: initial;
}
.v-treeview-item .v-list-item-title {
    white-space: normal;
}
.v-treeview-item .v-list-item__prepend {
    align-self: flex-start;
    padding-top: 5px;
}
.button {
}
.icon {
    margin-left: 5px;
}
.treeview-title {
    width: 100%;
    padding: 4px 0;
}
.treeview-folder-title {
    display: flex;
    align-items: center;
}
.treeview-leaf-title {
    display: flex;
    align-items: flex-start;
    width: 100%;
    gap: 8px;
}
.calc-description {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-right: 5px;
    line-height: 1.2rem;
}
</style>
